/**
 * Shared plumbing for scenery visuals.
 *
 * A level carries 150-400 props, so the budget per prop is brutal: a handful of
 * triangles, one or two draw calls, no per-frame work at all. Three things buy
 * that here.
 *
 * 1. **Everything is shared and reference counted.** Geometry and materials are
 *    built once per (kind, level) and freed when the last prop holding them is
 *    disposed — the same contract `entityviews.ts` uses, widened by a level key
 *    because a cardboard box has to be a different brown in every level.
 * 2. **Variants are baked, not computed.** A kind ships a small array of
 *    pre-merged geometries — upright, tipped, on its side — and a prop picks one
 *    from `PropSpot.variant`. Nothing is rotated or rebuilt at placement time,
 *    and a fallen chair still rests exactly on the floor because the fall was
 *    baked in and the result settled onto y = 0 while it was being built.
 * 3. **Parts are merged.** A chair is one buffer per material, not six meshes.
 *
 * Props never move after `place`, so they turn off `matrixAutoUpdate` and
 * compose their matrix once.
 */

import * as THREE from 'three';
import { hash1, lerp, renderYaw } from '../core/math';
import type { LevelSpec, PropSpot } from '../core/types';
import type { AtlasEntry } from './propdecals';

/* ------------------------------------------------------------------ *
 * Reference counting
 * ------------------------------------------------------------------ */

export interface Disposable {
  dispose(): void;
}

export function freeAll(items: Iterable<Disposable | null | undefined>): void {
  for (const item of items) item?.dispose();
}

/**
 * Lazily built, reference-counted resources for one prop kind, keyed by level.
 *
 * `spec.id` is the key rather than the whole spec: the deeper loops of a level
 * (`world/levels.ts` re-derives specs past the authored list) change threat and
 * supply numbers but never the palette or the geometry profile, so they share
 * one bundle with the level they are a repeat of. Two levels are never resident
 * at once in practice, but keying by level is what makes that an optimisation
 * rather than an assumption.
 */
export class SpecKit<T extends Disposable> {
  private readonly entries = new Map<string, { value: T; refs: number }>();

  constructor(private readonly build: (spec: LevelSpec) => T) {}

  acquire(spec: LevelSpec): T {
    let entry = this.entries.get(spec.id);
    if (!entry) {
      entry = { value: this.build(spec), refs: 0 };
      this.entries.set(spec.id, entry);
    }
    entry.refs++;
    return entry.value;
  }

  release(spec: LevelSpec): void {
    const entry = this.entries.get(spec.id);
    if (!entry) return;
    if (--entry.refs <= 0) {
      // Drop the map slot too, so a long session that walks a dozen levels does
      // not accumulate empty entries.
      this.entries.delete(spec.id);
      entry.value.dispose();
    }
  }
}

/* ------------------------------------------------------------------ *
 * Deterministic per-instance variation
 * ------------------------------------------------------------------ */

/**
 * Pick one of `count` baked variants from a prop's variation seed.
 *
 * `PropSpot.variant` is documented only as "a per-prop deterministic variation
 * seed", so this accepts both conventions a placer might reasonably use: a
 * fraction in [0, 1) or an integer counter.
 */
export function variantIndex(variant: number, count: number): number {
  if (count <= 1) return 0;
  const v = Number.isFinite(variant) ? Math.abs(variant) : 0;
  const i = v < 1 ? Math.floor(v * count) : Math.floor(v) % count;
  return i >= count ? count - 1 : i;
}

/** A stable 0..1 draw from a prop's variation seed. Different `salt`, different draw. */
export function vrand(variant: number, salt: number): number {
  return hash1(Math.round((Number.isFinite(variant) ? variant : 0) * 1024) | 0, salt);
}

/** A stable -1..1 draw. */
export function vsigned(variant: number, salt: number): number {
  return vrand(variant, salt) * 2 - 1;
}

/* ------------------------------------------------------------------ *
 * Geometry helpers
 * ------------------------------------------------------------------ */

/** A box, translated so `x, y, z` is its centre. */
export function box(w: number, h: number, d: number, x = 0, y = 0, z = 0): THREE.BufferGeometry {
  const g = new THREE.BoxGeometry(w, h, d);
  if (x || y || z) g.translate(x, y, z);
  return g;
}

/** A cylinder standing on +Y, translated so `x, y, z` is its centre. */
export function cyl(
  radiusTop: number,
  radiusBottom: number,
  height: number,
  segments: number,
  x = 0,
  y = 0,
  z = 0,
  open = false
): THREE.BufferGeometry {
  const g = new THREE.CylinderGeometry(radiusTop, radiusBottom, height, segments, 1, open);
  if (x || y || z) g.translate(x, y, z);
  return g;
}

const _up = new THREE.Vector3(0, 1, 0);
const _from = new THREE.Vector3();
const _to = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _quat = new THREE.Quaternion();

/**
 * A capless rod between two points. Cables, pipes, ladder rungs and pool-ladder
 * bends are all this, which is why it is worth the quaternion.
 */
export function strut(
  radius: number,
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  segments = 5
): THREE.BufferGeometry {
  _from.set(ax, ay, az);
  _to.set(bx, by, bz);
  _dir.subVectors(_to, _from);
  const length = _dir.length() || 1e-4;
  _dir.multiplyScalar(1 / length);
  const g = new THREE.CylinderGeometry(radius, radius, length, segments, 1, true);
  _quat.setFromUnitVectors(_up, _dir);
  g.applyQuaternion(_quat);
  g.translate((ax + bx) / 2, (ay + by) / 2, (az + bz) / 2);
  return g;
}

/** A flat quad lying in the XZ plane, face up. Paper, decals, anything shed. */
export function slab(w: number, d: number, x = 0, y = 0, z = 0, yaw = 0): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(w, d);
  g.rotateX(-Math.PI / 2);
  if (yaw) g.rotateY(yaw);
  g.translate(x, y, z);
  return g;
}

/**
 * Concatenate parts into one buffer, position and normal only, disposing the
 * inputs. Most merged geometry carries no map — a decal is a single unmerged
 * plane — so dropping UVs costs nothing and halves the buffer. The exception is
 * the architecture in `propwalls.ts`, which is textured and many-sided; that
 * uses `mergeUV`.
 */
export function merge(parts: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  const flat = parts.map((p) => (p.index ? p.toNonIndexed() : p));
  let count = 0;
  for (const p of flat) count += p.attributes.position.count;

  const position = new Float32Array(count * 3);
  const normal = new Float32Array(count * 3);
  let offset = 0;
  for (const p of flat) {
    // Everything reaching here is built above from three's primitives, so the
    // attributes are non-interleaved Float32.
    const pa = p.attributes.position as THREE.BufferAttribute;
    const na = p.attributes.normal as THREE.BufferAttribute;
    position.set(pa.array as Float32Array, offset * 3);
    normal.set(na.array as Float32Array, offset * 3);
    offset += pa.count;
  }

  for (let i = 0; i < flat.length; i++) {
    flat[i]!.dispose();
    if (flat[i] !== parts[i]) parts[i]!.dispose();
  }

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(position, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(normal, 3));
  return out;
}

/**
 * As `merge`, but keeping UVs.
 *
 * A half-wall is a dozen quads that all have to sample the level's wallpaper at
 * the right pitch, so its UVs are the whole point and it cannot go through
 * `merge`. Parts without a `uv` attribute are padded with zeroes rather than
 * rejected, so an untextured detail can be merged into a textured run.
 */
export function mergeUV(parts: readonly THREE.BufferGeometry[]): THREE.BufferGeometry {
  const flat = parts.map((p) => (p.index ? p.toNonIndexed() : p));
  let count = 0;
  for (const p of flat) count += p.attributes.position.count;

  const position = new Float32Array(count * 3);
  const normal = new Float32Array(count * 3);
  const uv = new Float32Array(count * 2);
  let offset = 0;
  for (const p of flat) {
    const pa = p.attributes.position as THREE.BufferAttribute;
    const na = p.attributes.normal as THREE.BufferAttribute;
    const ua = p.attributes.uv as THREE.BufferAttribute | undefined;
    position.set(pa.array as Float32Array, offset * 3);
    normal.set(na.array as Float32Array, offset * 3);
    if (ua) uv.set(ua.array as Float32Array, offset * 2);
    offset += pa.count;
  }

  for (let i = 0; i < flat.length; i++) {
    flat[i]!.dispose();
    if (flat[i] !== parts[i]) parts[i]!.dispose();
  }

  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(position, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(normal, 3));
  out.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  return out;
}

/**
 * Point a geometry's UVs at one row of a decal atlas.
 *
 * Every primitive built above lays its UVs out over 0..1 per face, so this works
 * on a box or a rod as well as on the plane it was written for — which is how
 * one drawn hazard stripe ends up on a lip, a fascia and a length of tape.
 */
export function remapUV(geo: THREE.BufferGeometry, entry: AtlasEntry): void {
  const uv = geo.attributes.uv as THREE.BufferAttribute;
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, lerp(entry.u0, entry.u1, uv.getX(i)), lerp(entry.v0, entry.v1, uv.getY(i)));
  }
  uv.needsUpdate = true;
}

/**
 * A quad carrying one atlas row, facing out of the wall.
 *
 * Turning it a half turn about Y is what makes it face the room: `renderYaw`
 * maps a prop's facing to local -Z, and a `PlaneGeometry` is born facing +Z.
 * The half turn also mirrors X back, so the writing still reads left to right
 * from the only side anyone sees it from.
 */
export function wallDecal(entry: AtlasEntry, height: number, z: number, roll = 0): THREE.BufferGeometry {
  const geo = new THREE.PlaneGeometry(height * entry.aspect, height);
  remapUV(geo, entry);
  if (roll) geo.rotateZ(roll);
  geo.rotateY(Math.PI);
  geo.translate(0, 0, z);
  return geo;
}

/** Rotate geometries together, so the layers of one prop stay in register. */
export function tumble(geos: readonly THREE.BufferGeometry[], rx: number, rz: number, ry = 0): void {
  const m = new THREE.Matrix4().makeRotationFromEuler(new THREE.Euler(rx, ry, rz, 'YXZ'));
  for (const g of geos) {
    if (g.attributes.position) g.applyMatrix4(m);
  }
}

/**
 * Drop geometries together until the lowest of them rests at `y`. Called after
 * `tumble` so a knocked-over prop touches the floor instead of hovering over it
 * or sinking through — the whole reason falls are baked rather than applied to
 * the object transform at placement.
 */
export function settle(geos: readonly THREE.BufferGeometry[], y = 0): void {
  let min = Infinity;
  for (const g of geos) {
    if (!g.attributes.position) continue;
    g.computeBoundingBox();
    if (g.boundingBox) min = Math.min(min, g.boundingBox.min.y);
  }
  if (!Number.isFinite(min)) return;
  const dy = y - min;
  if (Math.abs(dy) < 1e-6) return;
  for (const g of geos) {
    if (g.attributes.position) g.translate(0, dy, 0);
  }
}

/* ------------------------------------------------------------------ *
 * Palette
 * ------------------------------------------------------------------ */

/**
 * A prop's materials, derived from the level it stands in.
 *
 * Nothing here is an absolute colour. Every base is pulled toward the level's
 * own air (`palette.fog`) so that scenery belongs to the room rather than
 * looking imported: the same cardboard box is warm brown under the lobby's
 * yellow and grey-green in the poolrooms. The exceptions are deliberate —
 * `blanket`, `carton` and `hazard` are things a person carried in, and they are
 * barely tinted at all, which is exactly what makes them read as intrusions.
 */
export interface PropPalette {
  air: THREE.Color;
  cardboard: THREE.Color;
  paper: THREE.Color;
  wood: THREE.Color;
  woodDark: THREE.Color;
  metal: THREE.Color;
  metalDark: THREE.Color;
  rust: THREE.Color;
  plastic: THREE.Color;
  plasticDark: THREE.Color;
  fabric: THREE.Color;
  pale: THREE.Color;
  dark: THREE.Color;
  glass: THREE.Color;
  chrome: THREE.Color;
  grime: THREE.Color;
  /** Things somebody brought with them. Nearly untinted on purpose. */
  blanket: THREE.Color;
  carton: THREE.Color;
  hazard: THREE.Color;
  /** How hard bases are pulled toward the level's air. Reused by the decal tints. */
  pull: number;
  gain: number;
  /** True when the level's walls are light enough that marks must be dark. */
  lightWalls: boolean;
}

/** Relative luminance of a colour already in linear working space. */
function luminance(c: THREE.Color): number {
  return c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722;
}

/**
 * Pull a base colour toward the level's air and scale it for how grimy the
 * level is. Exported because the decal tints need the same treatment.
 */
export function belong(hex: number, pal: PropPalette, pull = pal.pull, gain = pal.gain): THREE.Color {
  const c = new THREE.Color(hex);
  c.lerp(pal.air, pull);
  c.multiplyScalar(gain);
  return c;
}

/** A CSS hex string for a working-space colour, for handing to Canvas2D. */
export function css(c: THREE.Color): string {
  return `#${c.getHexString(THREE.SRGBColorSpace)}`;
}

/** How dirty and how tinted each level's scenery is. */
function profileFor(spec: LevelSpec): { pull: number; gain: number } {
  switch (spec.generator) {
    case 'warehouse':
      return { pull: 0.3, gain: 0.86 };
    case 'pipes':
    case 'station':
      // Everything down here has been wet for a long time.
      return { pull: 0.36, gain: 0.7 };
    case 'poolrooms':
      return { pull: 0.3, gain: 1.05 };
    // Nothing on Level 6 is lit well enough to have a colour. Pulling scenery
    // most of the way to the fog is what keeps a crate from being the
    // brightest object on the darkest level in the game.
    case 'lightsout':
      return { pull: 0.55, gain: 0.62 };
    // Daylight, and a lot of it. Level 11 is the only level whose scenery is
    // lit by something other than the building.
    case 'city':
      return { pull: 0.24, gain: 1.02 };
    // "Mostly clean, with little dust" — the best-maintained floor there is.
    case 'hotel':
      return { pull: 0.28, gain: 1.0 };
    case 'offices':
    default:
      return { pull: 0.33, gain: 0.97 };
  }
}

export function propPalette(spec: LevelSpec): PropPalette {
  const { pull, gain } = profileFor(spec);
  const air = new THREE.Color(spec.palette.fog);
  const base: PropPalette = {
    air,
    pull,
    gain,
    lightWalls: luminance(new THREE.Color(spec.palette.wall)) > 0.16,
    // Filled in below; declared here so `belong` can take the finished object.
    cardboard: air,
    paper: air,
    wood: air,
    woodDark: air,
    metal: air,
    metalDark: air,
    rust: air,
    plastic: air,
    plasticDark: air,
    fabric: air,
    pale: air,
    dark: air,
    glass: air,
    chrome: air,
    grime: air,
    blanket: air,
    carton: air,
    hazard: air,
  };

  base.cardboard = belong(0xb28a58, base);
  base.paper = belong(0xdcd5c0, base);
  base.wood = belong(0x8f7248, base);
  base.woodDark = belong(0x5f4b2e, base);
  base.metal = belong(0x9aa0a4, base);
  base.metalDark = belong(0x4f5357, base);
  base.rust = belong(0x8a5533, base);
  base.plastic = belong(0x8d8f8a, base);
  base.plasticDark = belong(0x3d403f, base);
  base.fabric = belong(0x7c7365, base);
  base.pale = belong(0xd6d3c9, base);
  base.dark = belong(0x242422, base, pull * 0.5, gain);
  // Glass is nearly a hole. Whatever is behind it is never modelled.
  base.glass = belong(0x161c1e, base, pull * 0.4, gain);
  base.chrome = belong(0xc2c8cd, base, pull * 0.45, 1.0);
  base.grime = belong(0x5b4a33, base, pull * 0.6, gain);
  // Brought in from outside. Tinted a third as hard as anything else.
  // A blanket should still be the warmest thing in the room — it is the one
  // object somebody chose to carry — but at a 0.3 pull it stayed pillar-box red
  // against mono-yellow wallpaper and read as a prop rather than as bedding.
  // Duller, browner, and pulled most of the way into the level's own light.
  base.blanket = belong(0x6d4335, base, pull * 0.62, 0.9);
  base.carton = belong(0xe6dcc2, base, pull * 0.3, 1.0);
  base.hazard = belong(0xc4632a, base, pull * 0.35, 1.0);
  return base;
}

/** Lit normally, plus a whisper of self-illumination so nothing is ever a hole. */
export function matte(color: THREE.Color, emissiveIntensity = 0.05): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color, emissive: color, emissiveIntensity });
}

/* ------------------------------------------------------------------ *
 * Anchoring
 * ------------------------------------------------------------------ */

/**
 * Where a kind lives, and how forgiving it is about the spot it is handed.
 *
 * Wall and ceiling props are authored with their mounting surface at the local
 * origin and their body running toward local -Z (which `renderYaw` maps to the
 * prop's facing direction), so a spot on a wall face puts them flush against it
 * whichever way the wall runs.
 */
export interface Anchor {
  kind: 'floor' | 'wall' | 'ceiling';
  /**
   * Fallback used when the spot's own height is on the floor but the kind is
   * not. A placer that leaves `height` at 0 for a sign still gets a sign at eye
   * level rather than one lying in the carpet.
   */
  height?: number;
  /**
   * Metres pushed off the mounting surface: out of the wall, up from the floor,
   * down from the ceiling. Decals use it to stay off the surface they sit on.
   */
  out?: number;
  /**
   * Radians of deterministic yaw wobble per instance. Nothing here is square.
   *
   * Render-only: `PropIndex` rotates the collision box by `PropSpot.yaw` alone,
   * so a jittered model turns inside a box that does not turn with it. The
   * footprints in `world/gen/propTables.ts` are measured with the jitter
   * applied and cover the worst case; keep it small, and re-measure if it
   * grows. Yaw the *placer* wants the collision to know about goes in
   * `spot.yaw`, where both sides can see it.
   */
  yawJitter?: number;
  /** Radians of deterministic lean per instance. Floor props that have settled. */
  tilt?: number;
  /**
   * Force the prop down onto the floor whatever height the spot carries. A wall
   * ladder that does not reach the floor is not a ladder.
   */
  grounded?: boolean;
  /**
   * This kind is taller than the level's ceiling on purpose, so the scale clamp
   * does not apply to it.
   *
   * `ceilingHeight` is one number for a whole level, and on a level with sky
   * over it — Level 94's lanes are `Cell.Open` — it is the height of the *houses*
   * rather than of anything above the street. Clamping a 12 m water tower into a
   * 3.4 m house would render it at 28% inside a collision box four times its
   * size. Set only where the kind's own comment says why.
   */
  unroofed?: boolean;
}

/**
 * Position, orient and freeze one prop. Called once, at level build.
 *
 * `top` is the authored height of the kind's highest point. It is what keeps
 * `PropSpot.scale` honest: a 15% larger shelving unit is fine in the lobby and
 * pushes through the ceiling in the pipes, so the scale is capped rather than
 * trusted. `Anchor.unroofed` opts out, for the things that stand outdoors.
 */
export function anchorProp(
  object: THREE.Object3D,
  spot: PropSpot,
  anchor: Anchor,
  ceilingHeight: number,
  top = 0
): void {
  const jitter = anchor.yawJitter ? vsigned(spot.variant, 0x51a3) * anchor.yawJitter : 0;
  const yaw = spot.yaw + jitter;
  const given = Number.isFinite(spot.height) ? spot.height : 0;
  const out = anchor.out ?? 0;

  let y = given;
  let x = spot.pos.x;
  let z = spot.pos.z;
  if (anchor.kind === 'ceiling') {
    y = (given > 0.2 ? given : ceilingHeight) - out;
  } else if (anchor.kind === 'wall') {
    if (anchor.height !== undefined && given < 0.05) y = anchor.height;
    // Out of the wall along the facing direction: forward = (sin yaw, -cos yaw).
    x += Math.sin(yaw) * out;
    z -= Math.cos(yaw) * out;
  } else {
    y = given + out;
  }
  if (anchor.grounded) y = out;

  let scale = Number.isFinite(spot.scale) && spot.scale > 0 ? spot.scale : 1;
  if (top > 0.01 && anchor.kind !== 'ceiling' && !anchor.unroofed) {
    scale = Math.min(scale, Math.max(0.2, (ceilingHeight - 0.04 - y) / top));
  }

  object.position.set(x, y, z);
  object.rotation.set(0, renderYaw(yaw), 0);
  if (anchor.tilt) {
    object.rotation.x = vsigned(spot.variant, 0x9d17) * anchor.tilt;
    object.rotation.z = vsigned(spot.variant, 0xc7b1) * anchor.tilt;
  }
  object.scale.setScalar(scale);

  // Scenery never moves again, so it pays for its matrix exactly once.
  object.matrixAutoUpdate = false;
  object.updateMatrix();
}

/* ------------------------------------------------------------------ *
 * Shapes
 * ------------------------------------------------------------------ */

/**
 * One material's worth of a kind: a geometry per variant, or `null` where that
 * variant simply does not have this part (an empty shelf, an unbroken crate).
 */
export interface PropLayer {
  variants: ReadonlyArray<THREE.BufferGeometry | null>;
  material: THREE.Material;
}

/** Everything one kind shares across every instance of it on a level. */
export interface PropShape extends Disposable {
  layers: readonly PropLayer[];
  /** How many baked variants this kind has. */
  count: number;
  anchor: Anchor;
  ceilingHeight: number;
  /** Authored height of the tallest variant, for the scale clamp in `anchorProp`. */
  top: number;
}

/**
 * Bundle layers into a shape and take ownership of their GPU resources. Extra
 * disposables (canvas textures, mostly) are freed with it.
 */
export function makeShape(
  spec: LevelSpec,
  anchor: Anchor,
  layers: readonly PropLayer[],
  extra: readonly Disposable[] = []
): PropShape {
  let count = 1;
  let top = 0;
  for (const layer of layers) {
    count = Math.max(count, layer.variants.length);
    for (const geo of layer.variants) {
      if (!geo || !geo.attributes.position) continue;
      geo.computeBoundingBox();
      if (geo.boundingBox) top = Math.max(top, geo.boundingBox.max.y);
    }
  }
  return {
    layers,
    count,
    anchor,
    top,
    ceilingHeight: spec.ceilingHeight,
    dispose(): void {
      const owned = new Set<Disposable>();
      for (const layer of layers) {
        for (const geo of layer.variants) if (geo) owned.add(geo);
        owned.add(layer.material);
      }
      for (const item of extra) owned.add(item);
      freeAll(owned);
    },
  };
}
